
<template>
  <div>
    <validation-observer ref="transshipmentFormValidator">
      <v-form>
        <v-card class="rounded-card" flat>
          <v-card-title>契約情報編集</v-card-title>

          <v-card-text>
            <v-card flat>
              <v-card-title>法人編集</v-card-title>
              <v-card-text>
                <!-- パスワード有効期限 -->
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="contractor.password_valid_day"
                      :items="contractor.password_valid_days"
                      dense
                      label="パスワード有効期限"
                      outlined
                      persistent-placeholder
                      hint="パスワードの定期変更を可能とします。有効期限を迎えるとログイン後にパスワード変更画面が表示されます。"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
                <!-- Webアクセストークン有効期限 -->
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="contractor.web_access_token_valid_time"
                      :items="contractor.web_access_token_valid_times"
                      dense
                      label="WEBアクセストークン有効期限"
                      outlined
                      persistent-placeholder
                      hint="WEBログイン後、設定した時間を経過すると自動ログアウトされます。"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
                <!-- アプリアクセストークン有効期限 -->
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="contractor.app_access_token_valid_time"
                      :items="contractor.app_access_token_valid_times"
                      dense
                      label="アプリアクセストークン有効期限"
                      outlined
                      persistent-placeholder
                      hint="アプリログイン後、設定した時間を経過すると自動ログアウトされます。"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider class="my-4" color="light-blue lighten-5"></v-divider>
            <v-card flat>
              <v-card-title>支店/営業所編集</v-card-title>
              <v-card-text>
                <v-subheader class="px-0 text-h6 font-weight-medium">EDI連携申込Webメール内容</v-subheader>
                <!-- メール件名 -->
                <v-row>
                  <v-col cols="12" md="8">
                    <validation-provider
                      v-slot="{ errors }"
                      name="メール件名"
                      rules="required|max:255"
                      class="requiredMark"
                    >
                      <v-text-field
                        v-model="tenant.mail_templates_attributes[0].title"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="メール件名"
                        outlined
                        persistent-placeholder
                        placeholder="【${from_company}】JWNET-EDI連携サービス利用申し込みのお知らせ"
                      >
                        <template v-slot:label>
                          <span>メール件名</span>
                          <span class="ml-1">
                            ({{ tenant.mail_templates_attributes[0].title?.length || 0 }} / 255)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- /メール件名 -->
                <!-- メール内容 -->
                <v-row>
                  <v-col cols="12" md="8">
                    <validation-provider
                      v-slot="{ errors }"
                      name="メール内容"
                      rules="required|max:2000|mail_template_url"
                      class="requiredMark"
                    >
                      <v-textarea
                        v-model="tenant.mail_templates_attributes[0].body"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="メール内容"
                        outlined
                        persistent-placeholder
                        rows="10"
                      >
                        <template v-slot:label>
                          <span>メール内容</span>
                          <span class="ml-1">
                            ({{ tenant.mail_templates_attributes[0].body?.length || 0 }} / 2000)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                      </v-textarea>
                    </validation-provider>
                    <v-alert
                      dense
                      text
                      type="info"
                      class="mt-2"
                    >
                      <div class="text-body-2">
                        本文には以下の置換文字列を含めてください。
                      </div>
                      <div class="mt-2">
                        <div><code>${url}</code> → EDI連携申込URL</div>
                        <div><code>${from_company}</code> → 自社名</div>
                        <div><code>${to_company}</code> → 送信先社名</div>
                        <div><code>${to_person}</code> → 送信先担当者名</div>
                      </div>
                    </v-alert>
                  </v-col>
                </v-row>
                <!-- /メール内容 -->

                <template v-if="useBilling">
                  <v-divider class="my-4" color="light-blue lighten-5"></v-divider>
                  <v-subheader class="px-0 text-h6 font-weight-medium">請求書設定</v-subheader>
                  <!-- 登録番号（適格請求書発行事業者） -->
                  <v-row>
                    <v-col cols="12" md="4">
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{ alpha_num_only: true, length: 14 }"
                        name="登録番号（適格請求書発行事業者）"
                      >
                        <v-text-field
                          v-model="tenant.register_number"
                          :error-messages="errors"
                          dense
                          hide-details="auto"
                          label="登録番号（適格請求書発行事業者）"
                          outlined
                          persistent-placeholder
                          placeholder="例）T1234567890123"
                        >
                          <template v-slot:label>
                            <span>登録番号（適格請求書発行事業者）</span>
                            <span class="ml-1">
                              ({{ tenant.register_number?.length || 0 }} / 14)
                            </span>
                          </template>
                          <template v-slot:counter></template>
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <!-- /登録番号（適格請求書発行事業者） -->
                  <v-card class="rounded-card" outlined>
                    <v-card-text>
                      <v-subheader class="px-0 text-subtitle-1 font-weight-medium">振込先1</v-subheader>
                      <!-- 振込先 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            rules="max:25"
                            name="振込先1"
                          >
                            <v-text-field
                              v-model="tenant.payee"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込先1"
                              outlined
                              persistent-placeholder
                              placeholder="例）〇〇銀行〇〇支店"
                            >
                              <template v-slot:label>
                                <span>振込先1</span>
                                <span class="ml-1">
                                  ({{ tenant.payee?.length || 0 }} / 25)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込先 -->
                      <!-- 口座種別／口座番号 -->
                      <v-row>
                        <v-col cols="12" md="2">
                          <v-select
                            v-model="tenant.account_type"
                            :items="tenant.account_types"
                            dense
                            label="口座種別1"
                            outlined
                            persistent-placeholder
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            :rules="{ integer: true, length: 7 }"
                            name="口座番号1"
                          >
                            <v-text-field
                              v-model="tenant.account_number"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="口座番号1"
                              outlined
                              persistent-placeholder
                              placeholder="例）1234567"
                            >
                              <template v-slot:label>
                                <span>口座番号1</span>
                                <span class="ml-1">
                                  ({{ tenant.account_number?.length || 0 }} / 7)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /口座種別／口座番号 -->
                      <!-- 振込口座名義 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            name="振込口座名義1"
                            rules="full_width|max:40"
                          >
                            <v-text-field
                              v-model="tenant.account_name"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込口座名義1"
                              outlined
                              persistent-placeholder
                              placeholder="例）エービーシーシュウウン"
                            >
                              <template v-slot:label>
                                <span>振込口座名義1</span>
                                <span class="ml-1">
                                  ({{ tenant.account_name?.length || 0 }} / 40)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込口座名義 -->
                    </v-card-text>
                  </v-card>
                  <br />

                  <v-card class="rounded-card" outlined>
                    <v-card-text>
                      <v-subheader class="px-0 text-subtitle-1 font-weight-medium">振込先2</v-subheader>
                      <!-- 振込先2 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            rules="max:25"
                            name="振込先2">
                            <v-text-field
                              v-model="tenant.payee_2"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込先2"
                              outlined
                              persistent-placeholder
                              placeholder="例）〇〇銀行〇〇支店"
                            >
                              <template v-slot:label>
                                <span>振込先2</span>
                                <span class="ml-1">
                                  ({{ tenant.payee_2?.length || 0 }} / 25)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込先2 -->
                      <!-- 口座種別2／口座番号2 -->
                      <v-row>
                        <v-col cols="12" md="2">
                          <v-select
                            v-model="tenant.account_type_2"
                            :items="tenant.account_type_2s"
                            dense
                            label="口座種別2"
                            outlined
                            persistent-placeholder
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            :rules="{ integer: true, length: 7 }"
                            name="口座番号2"
                          >
                            <v-text-field
                              v-model="tenant.account_number_2"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="口座番号2"
                              outlined
                              persistent-placeholder
                              placeholder="例）1234567"
                            >
                              <template v-slot:label>
                                <span>口座番号2</span>
                                <span class="ml-1">
                                  ({{ tenant.account_number_2?.length || 0 }} / 7)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /口座種別2／口座番号2 -->
                      <!-- 振込口座名義2 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            name="振込口座名義2"
                            rules="full_width|max:40"
                          >
                            <v-text-field
                              v-model="tenant.account_name_2"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込口座名義2"
                              outlined
                              persistent-placeholder
                              placeholder="例）エービーシーシュウウン"
                            >
                              <template v-slot:label>
                                <span>振込口座名義2</span>
                                <span class="ml-1">
                                  ({{ tenant.account_name_2?.length || 0 }} / 40)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込口座名義2 -->
                    </v-card-text>
                  </v-card>
                  <br />

                  <v-card class="rounded-card" outlined>
                    <v-card-text>
                      <v-subheader class="px-0 text-subtitle-1 font-weight-medium">振込先3</v-subheader>
                      <!-- 振込先3 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            rules="max:25"
                            name="振込先3"
                          >
                            <v-text-field
                              v-model="tenant.payee_3"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込先3"
                              outlined
                              persistent-placeholder
                              placeholder="例）〇〇銀行〇〇支店"
                            >
                              <template v-slot:label>
                                <span>振込先3</span>
                                <span class="ml-1">
                                  ({{ tenant.payee_3?.length || 0 }} / 25)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込先3 -->
                      <!-- 口座種別3／口座番号3 -->
                      <v-row>
                        <v-col cols="12" md="2">
                          <v-select
                            v-model="tenant.account_type_3"
                            :items="tenant.account_type_3s"
                            dense
                            label="口座種別3"
                            outlined
                            persistent-placeholder
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            :rules="{ integer: true, length: 7 }"
                            name="口座番号3"
                          >
                            <v-text-field
                              v-model="tenant.account_number_3"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="口座番号3"
                              outlined
                              persistent-placeholder
                              placeholder="例）1234567"
                            >
                              <template v-slot:label>
                                <span>口座番号3</span>
                                <span class="ml-1">
                                  ({{ tenant.account_number_3?.length || 0 }} / 7)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /口座種別3／口座番号3 -->
                      <!-- 振込口座名義3 -->
                      <v-row>
                        <v-col cols="12" md="4">
                          <validation-provider
                            v-slot="{ errors }"
                            name="振込口座名義3"
                            rules="full_width|max:40"
                          >
                            <v-text-field
                              v-model="tenant.account_name_3"
                              :error-messages="errors"
                              dense
                              hide-details="auto"
                              label="振込口座名義3"
                              outlined
                              persistent-placeholder
                              placeholder="例）エービーシーシュウウン"
                            >
                              <template v-slot:label>
                                <span>振込口座名義3</span>
                                <span class="ml-1">
                                  ({{ tenant.account_name_3?.length || 0 }} / 40)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <!-- /振込口座名義3 -->
                    </v-card-text>
                  </v-card>
                  <br />

                  <!-- 備考 -->
                  <v-row>
                    <v-col cols="12" md="6">
                      <validation-provider
                        v-slot="{ errors }"
                        name="備考"
                        rules="max:40"
                      >
                        <v-text-field
                          v-model="tenant.note"
                          :error-messages="errors"
                          dense
                          hide-details="auto"
                          label="備考"
                          outlined
                          persistent-placeholder
                        >
                          <template v-slot:label>
                            <span>備考</span>
                            <span class="ml-1">
                              ({{ tenant.note?.length || 0 }} / 40)
                            </span>
                          </template>
                          <template v-slot:counter></template>
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <!-- /備考 -->
                </template>
              </v-card-text>
            </v-card>

            <v-divider class="my-4" color="light-blue lighten-5"></v-divider>
            <v-card flat>
              <v-card-text>
                <v-subheader class="px-0 text-h6 font-weight-medium">数量の確定者</v-subheader>
                <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="tenant.quantity_confirm_id"
                          :items="quantityConfirms"
                          clearable
                          dense
                          label="数量の確定者"
                          outlined
                          persistent-placeholder
                        />
                      </v-col>
                    </v-row>
                </v-card-text>
              </v-card>

              <v-divider class="my-4" color="light-blue lighten-5"></v-divider>
              <v-card flat>
                <v-card-text>
                  <v-subheader class="px-0 text-h6 font-weight-medium">運搬・処分・最終処分報告通知設定</v-subheader>
                <v-row>
                  <v-col cols="12" md="4">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ numeric: true, min_value: 1, max_value: 180 }"
                      name="運搬日数"
                    >
                      <v-text-field
                        v-model="tenant.transport_day"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="運搬日数"
                        outlined
                        persistent-placeholder
                        hint="交付日から運搬報告未報告通知する日数を入力します。"
                        persistent-hint
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ numeric: true, min_value: 1, max_value: 180 }"
                      name="処分日数"
                    >
                      <v-text-field
                        v-model="tenant.disposal_day"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="処分日数"
                        outlined
                        persistent-placeholder
                        hint="交付日から処分終了未報告通知する日数を入力します。"
                        persistent-hint
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ numeric: true, min_value: 1, max_value: 180 }"
                      name="最終処分日数"
                    >
                      <v-text-field
                        v-model="tenant.final_disposal_day"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="最終処分日数"
                        outlined
                        persistent-placeholder
                        hint="交付日から最終処分終了未報告通知する日数を入力します。"
                        persistent-hint
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider class="my-4" color="light-blue lighten-5"></v-divider>
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ required: true, numeric: true, min_value: 1, max_value: 99 }"
                      name="受注表示期間"
                      class="requiredMark"
                    >
                      <v-text-field
                        v-model="tenant.search_start_before_month"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="受注表示期間"
                        outlined
                        persistent-placeholder
                        hint="入力した月数を受注検索対象とします。"
                        persistent-hint
                      >
                        <template v-slot:label>
                          <span>受注表示期間</span>
                          <span class="ml-1">
                            ({{ tenant.search_start_before_month?.length || 0 }} / 2)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row v-if="tenant.use_api && isSuperAdminUser">
                  <v-col cols="12" md="6">
                    <validation-provider
                      v-slot="{ errors }"
                      name="APIキー"
                    >
                      <v-text-field
                        v-model="tenant.api_key"
                        :error-messages="errors"
                        label="APIキー"
                        hide-details="auto"
                        dense
                        readonly
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:append-outer>
                          <v-btn
                            outlined
                            small
                            @click="makeAPIKeyButtonClickEvent"
                          >
                            APIキー生成
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeButtonClickEvent">
              閉じる
            </v-btn>
            <v-btn color="primary" @click="registerButtonClickEvent">
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </validation-observer>
  </div>
</template>

<script>

import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'TransshipmentForm',
  components: {},
  mixins: [],
  data() {
    return {
      tenant: {
        search_start_before_month: null,
        register_number: null,
        payee: null,
        account_type: null,
        account_number: null,
        account_name: null,
        payee_2: null,
        account_type_2: null,
        account_number_2: null,
        account_name_2: null,
        payee_3: null,
        account_type_3: null,
        account_number_3: null,
        account_name_3: null,
        quantity_confirm_id: null,
        transport_day: null,
        disposal_day: null,
        final_disposal_day: null,
        note: null,
        api_key: null,
        mail_templates_attributes: [{
          id: null,
          title: null,
          body: null,
        }],
      },
      quantityConfirms: [],
      contractor: {
        password_valid_day: null,
        web_access_token_valid_time: null,
        app_access_token_valid_time: null,
      },
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      useBilling: 'use_billing',
    }),
    ...mapGetters('DriverAuthentication', [
      'isSuperAdminUser',
    ]),
  },
  created() {
    this.editMasterTenant().then((content) => {
      this.tenant = content.tenant
      this.contractor = content.contractor
    })
    this.fetchQuantityConfirms().then((content) => {this.quantityConfirms = content.quantity_confirms})
  },
  methods: {
    ...mapActions('DriverMasterTenant', {
      editMasterTenant: 'edit',
      updateMasterTenant: 'update'
    }),
    ...mapActions('DriverMasterTenantApi', {
      createMasterTenantApi: 'show'
    }),
    ...mapActions('DriverMasterSearchQuantityConfirm', {
      fetchQuantityConfirms: 'index',
    }),
    registerButtonClickEvent() {
      let $self = this
      this.$refs.transshipmentFormValidator.validate().then((result) => {
        if (result) {
          $self.sendRegistrationInformation()
        }
      })
    },
    closeButtonClickEvent() {
      this.$router.push({ name: 'DriverMasterTenant' })
    },
    sendRegistrationInformation() {
      let params = {
        search_start_before_month: this.tenant.search_start_before_month,
        register_number: this.tenant.register_number,
        payee: this.tenant.payee,
        account_type: this.tenant.account_type,
        account_number: this.tenant.account_number,
        account_name: this.tenant.account_name,
        payee_2: this.tenant.payee_2,
        account_type_2: this.tenant.account_type_2,
        account_number_2: this.tenant.account_number_2,
        account_name_2: this.tenant.account_name_2,
        payee_3: this.tenant.payee_3,
        account_type_3: this.tenant.account_type_3,
        account_number_3: this.tenant.account_number_3,
        account_name_3: this.tenant.account_name_3,
        quantity_confirm_id: this.tenant.quantity_confirm_id,
        transport_day: this.tenant.transport_day,
        disposal_day: this.tenant.disposal_day,
        final_disposal_day: this.tenant.final_disposal_day,
        note: this.tenant.note,
        api_key: this.tenant.api_key,
        mail_templates_attributes: this.tenant.mail_templates_attributes,
        password_valid_day: this.contractor.password_valid_day,
        web_access_token_valid_time: this.contractor.web_access_token_valid_time,
        app_access_token_valid_time: this.contractor.app_access_token_valid_time,
      }
      this.updateMasterTenant({ params: params }).then(() => {
        this.$router.push({ name: 'DriverMasterTenant' })
      })
    },
    makeAPIKeyButtonClickEvent () {
      this.createMasterTenantApi().then((content) => {
        if (content.exists) {
          this.tenant.api_key = content.api_key
        }
      })
    },
  }
}
</script>
